import Image from 'next/image';
import Link from 'next/link';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { logger } from '@/lib/logger';
import { useEffect, useState } from 'react';

/**
 * サインインモード
 */
export type SignInMode = 'oauth' | 'credentials';

/**
 * サインインフォームのプロパティ（OAuth モード）
 */
export interface SignInFormOAuthProps {
  /**
   * サインインモード
   */
  readonly mode: 'oauth';

  /**
   * ローディング状態
   */
  readonly loading: boolean;

  /**
   * OAuth サインインボタンクリック時のハンドラー
   */
  readonly onSignIn: () => void;

  /**
   * サインアップページへのリンク表示フラグ
   * @default true
   */
  readonly showSignUpLink?: boolean;

  /**
   * パスワードリセットページへのリンク表示フラグ
   * @default true
   */
  readonly showForgotPasswordLink?: boolean;

  /**
   * サインアップリンクのカスタム href
   * @default '/auth/signup'
   */
  readonly signUpHref?: string;

  /**
   * 追加の CSS クラス
   */
  readonly className?: string;
}

/**
 * サインインフォームのプロパティ（Credentials モード）
 */
export interface SignInFormCredentialsProps {
  /**
   * サインインモード
   */
  readonly mode: 'credentials';

  /**
   * ローディング状態
   */
  readonly loading: boolean;

  /**
   * Credentials サインイン時のハンドラー
   */
  readonly onSubmit: (credentials: { email: string; password: string }) => void;

  /**
   * SSO（Cognito Hosted UI / SAML / OIDC IdP）サインインボタンを
   * メール/パスワードフォームの下に表示する。Issue #2812 / ADR-0029
   * Phase 1 の F2 最小スコープ。
   *
   * 渡されない場合は SSO ボタンを表示しない（既存挙動）。SSO 強制
   * テナントのユーザーは IdP 経由でしかログインできないため、この
   * ボタンが入口となる。
   */
  readonly onSsoSignIn?: () => void;

  /**
   * サインアップページへのリンク表示フラグ
   * @default true
   */
  readonly showSignUpLink?: boolean;

  /**
   * パスワードリセットページへのリンク表示フラグ
   * @default true
   */
  readonly showForgotPasswordLink?: boolean;

  /**
   * サインアップリンクのカスタム href
   * @default '/auth/signup'
   */
  readonly signUpHref?: string;

  /**
   * 追加の CSS クラス
   */
  readonly className?: string;

  /**
   * 組織固有の見出し (Issue #6149)。ロゴの下に `<h1>` として表示する。
   * `/auth/signin?org=<slug>` から `/api/auth/login-config` を経由して渡される。
   * 未指定なら見出しは表示しない (既存デザインを維持)。
   */
  readonly loginTitle?: string;

  /**
   * 組織固有の説明文 (Issue #6149)。見出しの下に `<p>` として表示する。
   * 改行は `\n` を `<br>` に置換せず、`white-space: pre-line` で表現する。
   */
  readonly loginDescription?: string;

  /**
   * SSO ボタンのラベル (Issue #6149)。未指定時は「SSO でサインイン」。
   * `onSsoSignIn` が渡されているときのみ効く。
   */
  readonly ssoButtonLabel?: string;

  /**
   * true のときメール/パスワードフォームと「または」セパレータを完全に
   * 非表示にし、SSO ボタンだけを見せる (Issue #6149)。SSO 強制組織で
   * 通常ログイン導線を消したい場合に使う。`onSsoSignIn` が無い場合は
   * SSO ボタンも出ないため、事実上「ログイン方法なし」の空フォームに
   * なる点に注意（呼び出し側で SSO ハンドラを必ず渡す運用）。
   */
  readonly hideCredentials?: boolean;
}

/**
 * サインインフォームのプロパティ（判別可能な Union 型）
 */
export type SignInFormProps = SignInFormOAuthProps | SignInFormCredentialsProps;

/**
 * サインインフォームコンポーネント
 *
 * 機能:
 * - OAuth モード: Cognito OAuth サインインボタン
 * - Credentials モード: メールアドレス・パスワード入力フォーム
 * - サインアップへのリンク (/auth/signup)
 * - パスワードリセットへのリンク (/auth/forgot-password)
 * - ローディング状態の表示
 * - アクセシビリティ対応
 *
 * @example OAuth モード
 * ```tsx
 * <SignInForm
 *   mode="oauth"
 *   loading={isLoading}
 *   onSignIn={handleOAuthSignIn}
 * />
 * ```
 *
 * @example Credentials モード
 * ```tsx
 * <SignInForm
 *   mode="credentials"
 *   loading={isLoading}
 *   onSubmit={handleCredentialsSignIn}
 * />
 * ```
 */
export const SignInForm: React.FC<SignInFormProps> = (props) => {
  const { loading, showSignUpLink = true, showForgotPasswordLink = true, className = '' } = props;
  const signUpHref = 'signUpHref' in props && props.signUpHref ? props.signUpHref : '/auth/signup';

  // 組織向けカスタマイズ (Issue #6149)。credentials モードのみ効く。
  const loginTitle = props.mode === 'credentials' ? props.loginTitle : undefined;
  const loginDescription = props.mode === 'credentials' ? props.loginDescription : undefined;
  const ssoButtonLabel =
    props.mode === 'credentials' ? props.ssoButtonLabel ?? 'SSO でサインイン' : 'SSO でサインイン';
  // Fail-safe: `hideCredentials=true` は `onSsoSignIn` があるときのみ有効。
  // 両方揃わないとログイン方法が完全に無くなる (「空フォーム」状態) ため、
  // SSO ハンドラ未指定なら hideCredentials は無視して通常フォームを出す。
  // 現在の呼び出し元 (`page.tsx`) は常に onSsoSignIn を渡すので実運用への影響なし。
  const hideCredentialsRequested = props.mode === 'credentials' && props.hideCredentials === true;
  const hasSsoHandler = props.mode === 'credentials' && Boolean(props.onSsoSignIn);
  const hideCredentials = hideCredentialsRequested && hasSsoHandler;
  // 警告ログは render 中ではなく effect で出す (StrictMode 二重発火・再 render 連打を防ぐ)。
  const shouldWarnMisuse = hideCredentialsRequested && !hasSsoHandler;
  useEffect(() => {
    if (shouldWarnMisuse) {
      logger.warn(
        '[SignInForm] hideCredentials=true but onSsoSignIn is missing; falling back to credentials form to avoid an empty login page.',
      );
    }
  }, [shouldWarnMisuse]);

  // Credentials モード用のローカル状態
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (props.mode === 'credentials') {
      props.onSubmit({ email, password });
    }
  };

  return (
    <div className={`bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 ${className}`}>
      <div className="space-y-6">
        {/* Kakusill ロゴ */}
        <div className="flex justify-center mb-8">
          <Image
            src="/images/logo.svg"
            alt="Kakusill Logo"
            width={480}
            height={90}
            className="h-16 w-auto"
            priority
          />
        </div>

        {/* 組織向けカスタム見出し / 説明 (Issue #6149) */}
        {loginTitle ? (
          <h1 className="text-center text-2xl font-bold text-gray-900">{loginTitle}</h1>
        ) : null}
        {loginDescription ? (
          <p className="text-center text-sm text-gray-600 whitespace-pre-line">{loginDescription}</p>
        ) : null}

        {props.mode === 'oauth' ? (
          /* OAuth モード: サインインボタン */
          <button
            onClick={props.onSignIn}
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            aria-label={loading ? 'サインイン処理中' : 'Kakusillにサインイン'}
          >
            {loading ? <LoadingSpinner srText="サインイン処理中..." /> : 'Kakusillにサインイン'}
          </button>
        ) : (
          /* Credentials モード: メールアドレス・パスワードフォーム */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* メール / パスワード / サインインボタンは hideCredentials=true (Issue #6149)
                の組織では完全非表示にする。SSO 強制組織で通常ログイン導線を消したいケース。 */}
            {!hideCredentials && (
              <>
                {/* メールアドレス */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    メールアドレス
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* パスワード */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    パスワード
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {/* サインインボタン */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  aria-label={loading ? 'サインイン処理中' : 'サインイン'}
                >
                  {loading ? <LoadingSpinner srText="サインイン処理中..." /> : 'サインイン'}
                </button>
              </>
            )}

            {/* SSO サインインボタン（Issue #2812 / ADR-0029 Phase 1 / F2）。
                onSsoSignIn が渡されたときのみ表示する。SSO 強制テナントの
                ユーザーは Cognito Hosted UI 経由 → IdP リダイレクトでしか
                ログインできないため、ここを入口にする。 */}
            {props.mode === 'credentials' && props.onSsoSignIn ? (
              <>
                {/* hideCredentials=true では通常ログイン導線を消しているため
                    「または」セパレータも出さない (Issue #6149) */}
                {!hideCredentials && (
                  <div className="relative" aria-hidden="true">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-gray-500">または</span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={props.onSsoSignIn}
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  aria-label={`${ssoButtonLabel}（シングルサインオン）`}
                >
                  {ssoButtonLabel}
                </button>
              </>
            ) : null}
          </form>
        )}

        {/* リンクセクション。hideCredentials=true (SSO 強制組織) では
            「パスワードを忘れた」もリセットしても結局ログインできないため隠す。
            サインアップリンクも credentials 前提なので同様に隠す (Issue #6149)。 */}
        {(showForgotPasswordLink || showSignUpLink) && !hideCredentials && (
          <div className="flex items-center justify-between text-sm">
            {showForgotPasswordLink && (
              <Link
                href="/auth/forgot-password"
                className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-150"
              >
                パスワードを忘れた
              </Link>
            )}

            {showSignUpLink && (
              <Link
                href={signUpHref}
                className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-150"
              >
                アカウントを作成
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
